import { ref } from 'vue';
import directus from './useDirectus.js';
import { useUserStore } from '../store/user.js';

const notifications = ref([]);
const notificationsCount = ref(0);

// Deduplication cache to prevent duplicate notifications
const recentNotifications = new Map();
const DEDUP_TIMEOUT_MS = 10000; // 10 seconds window for deduplication

// In-flight dedup so concurrent callers share one network request.
let inFlightGetNotifications = null;

// Refresh strategy: no clock-driven interval. Notifications are refetched on
// (a) tab focus, (b) every route change (hooked in main.js), and
// (c) after mutations (addNotification / updateNotification / markAsRead).
// Idle tabs cost zero requests.
let focusListenerBound = false;
const handleFocus = () => { getNotifications(); };

const getNotifications = async () => {
  const userStore = useUserStore();
  if (!userStore.isLoggedIn) return;

  // Concurrent callers (route change + focus + markAsRead) share one in-flight
  // promise instead of stacking identical requests.
  if (inFlightGetNotifications) return inFlightGetNotifications;

  inFlightGetNotifications = (async () => {
    try {
      const result = await directus.items('orbit_notification').readByQuery({
        fields: ['*'],
        limit: -1,
        meta: '*',
        filter: {
          status: { _eq: 'new' },
          recipient: { _eq: '$CURRENT_USER' },
        }
      });
      notifications.value = result.data;
      notificationsCount.value = result.meta.filter_count;
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      inFlightGetNotifications = null;
    }
  })();

  return inFlightGetNotifications;
};

export default function useNotification() {

  // Generate a unique key for deduplication
  const getNotificationKey = (title, recipient, link, collection) => {
    return `${title}|${recipient}|${link}|${collection}`;
  };

  const addNotification = async (title, sender, recipient, link, collection, message) => {
    // Create deduplication key
    const dedupKey = getNotificationKey(title, recipient, link, collection);

    // Check if this notification was recently created
    if (recentNotifications.has(dedupKey)) {
      console.log('Duplicate notification prevented:', title);
      return;
    }

    // Mark this notification as being processed
    recentNotifications.set(dedupKey, Date.now());

    // Clear from cache after timeout
    setTimeout(() => {
      recentNotifications.delete(dedupKey);
    }, DEDUP_TIMEOUT_MS);

    try {
      await directus.items('orbit_notification').createOne({
        title: title,
        sender: sender,
        recipient: recipient,
        link: link,
        collection: collection,
        message: message
      });
    } catch (error) {
      console.error("Error adding notification:", error);
      // Remove from cache on error so it can be retried
      recentNotifications.delete(dedupKey);
    }
  }

  const updateNotification = async (item) => {
    if (item.status === 'new') {
      const id = item.id;
      try {
        await directus.items('orbit_notification').updateOne(id, {
          status: 'read',
        });
        getNotifications();
      } catch (error) {
        console.error("Error updating notification:", error);
      }
    }
  }

  // Bind the tab-focus refresh listener (idempotent). The route-change hook
  // lives in main.js since it needs the router instance.
  const startNotificationsRefresh = () => {
    const userStore = useUserStore();
    if (!userStore.isLoggedIn) return;
    if (!focusListenerBound) {
      window.addEventListener('focus', handleFocus);
      focusListenerBound = true;
    }
  }

  const stopNotificationsRefresh = () => {
    if (focusListenerBound) {
      window.removeEventListener('focus', handleFocus);
      focusListenerBound = false;
    }
  }

  return {
    addNotification,
    getNotifications,
    notifications,
    notificationsCount,
    updateNotification,
    // Kept the old names as aliases so existing callers (e.g. MainNav.vue) work unchanged.
    subscribeToNotifications: startNotificationsRefresh,
    unsubscribeFromNotifications: stopNotificationsRefresh,
    startNotificationsRefresh,
    stopNotificationsRefresh
  };
}
